import {
  ActivityOrderBy,
  Issue,
  IssueMessageType,
  Media,
  Message,
  Name,
  formatShortDate,
  formatShortDateOrSameDay,
  formatShortDateTimeOrSameDay,
  formatShortDateAndTimeOrSameDay,
  getFullName,
  getEffectiveCreationDate,
} from '@gripp/shared-logic';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { Colors } from '../../themes/colors';

export type IssueMessageHeader = {
  updatedBy: string;
  updatedAt: string;
  fontStyle: StyleProp<TextStyle>;
  avatarSize: number;
  avatar?: Media;
  avatarIcon?: string;
  avatarIconStyle?: StyleProp<ViewStyle>;
  avatarIconColor?: string;
};

export const AnonymousId = '00000000-0000-0000-0000-000000000000';
export const AnonymousName = 'Anonymous';
export const FunctionId = '99999999-9999-9999-9999-999999999999';
export const SystemName = 'Gripp';
/**
 * Shared synthetic "External Contact" account — author-of-record for
 * inbound-email (and, per plan 0023, inbound-phone) messages. Mirrors
 * `EXTERNAL_CONTACT_ACCOUNT_ID` in `backend-workspace/apps/gripp-api/src/shared/constants.ts`;
 * kept in sync manually since the frontend has no generated import path to
 * that backend module (same pattern as AnonymousId/FunctionId above, which
 * mirror backend-only account ids too).
 */
export const ExternalContactId = 'eeeeeeee-eeee-eeee-eeee-eeeeeeeeeeee';

/**
 * Display label for an External Contact-authored message when the sender's
 * address is unavailable — never "Gripp" (`SystemName`), which would
 * misattribute an outside sender as us.
 */
export const ExternalContactName = 'External Contact';

export type IssueAuditData = {
  who: string;
  when: string;
};
export const getIssueAuditData = (
  issue: Issue & { latestMessage?: any },
  orderBy: ActivityOrderBy
): IssueAuditData => {
  const isWeb = Platform.OS === 'web';

  let auditPrefix: string;
  let auditBy: any;
  let auditWhen: any;

  switch (orderBy) {
    case ActivityOrderBy.MostRecentReported:
    case ActivityOrderBy.CreationDate:
      auditPrefix = 'created';
      auditBy = (issue as any)[`${auditPrefix}By`];
      // Use the computed effective creation date from backend
      auditWhen =
        (issue as any).effectiveCreationDate ||
        (issue as any)[`${auditPrefix}At`];
      break;
    case ActivityOrderBy.ReminderDate:
      const nextReminderDate = (issue as any).nextReminderDate as
        | string
        | undefined;
      if (!nextReminderDate) {
        return { who: '------', when: '------' };
      }
      auditPrefix = 'created';
      auditBy = (issue as any)[`${auditPrefix}By`];
      auditWhen = nextReminderDate;
      break;
    default:
      auditPrefix = 'updated';
      auditBy = (issue as any)[`${auditPrefix}By`];
      auditWhen = (issue as any)[`${auditPrefix}At`];
      break;
  }

  let who;

  if (!auditBy) {
    who = AnonymousName;
  } else {
    who = getIssueFullName(auditBy.id, auditBy.name || undefined);
  }

  return {
    who,
    when:
      orderBy === ActivityOrderBy.ReminderDate
        ? formatShortDate(auditWhen)
        : !isWeb
        ? formatShortDateOrSameDay(auditWhen)
        : formatShortDateAndTimeOrSameDay(auditWhen),
  };
};

export const getIssueMessageTextStyle = (
  message: Message
): StyleProp<TextStyle> | undefined => {
  return message.type === IssueMessageType.UserMessage
    ? undefined
    : styles.systemText;
};

export const getIssueMessageHeader = (
  issueMessage: Message,
  issue?: Issue
): IssueMessageHeader => {
  const header: Partial<IssueMessageHeader> = {
    avatarSize: 34,
    updatedAt: formatShortDateTimeOrSameDay(issueMessage.updatedAt),
  };

  const isUserAuthoredType =
    issueMessage.type === IssueMessageType.UserMessage ||
    issueMessage.type === IssueMessageType.AccessRequestMessage ||
    issueMessage.type === IssueMessageType.AccessRequestResolved;

  // Inbound-email messages (plan 0024) are authored as the shared synthetic
  // External Contact account — checked ahead of the Anonymous/Function system
  // branch below, since updatedBy.id would otherwise fall through to it.
  if (isUserAuthoredType && issueMessage.updatedBy.id === ExternalContactId) {
    assignExternalContactHeader(header, issue);
  } else if (
    isUserAuthoredType &&
    ![AnonymousId, FunctionId].includes(issueMessage.updatedBy.id!)
  ) {
    assignUserHeader(header, issueMessage);
  } else {
    assignSystemHeader(header, issueMessage);
  }

  return header as IssueMessageHeader;
};

export const getIssueFullName = (
  id: string | undefined,
  name: Name | undefined
) => {
  switch (id) {
    case AnonymousId:
      return AnonymousName;
    case FunctionId:
      return SystemName;
    default:
      return name ? getFullName(name) : '';
  }
};

const assignUserHeader = (
  header: Partial<IssueMessageHeader>,
  issueMessage: Message
): void => {
  header.updatedBy = getFullName(issueMessage.updatedBy.name!);
  header.avatar = issueMessage.updatedBy.avatar;
};

/**
 * External Contact author (plan 0024): the External Contact account has no
 * real name/avatar to show, so surface the sender's address instead — the
 * Activity's `createdFromAddress`, stamped by ingestInboundEmail at creation
 * time. Falls back to the External Contact label if the Activity wasn't passed
 * in or predates the address field.
 */
const assignExternalContactHeader = (
  header: Partial<IssueMessageHeader>,
  issue?: Issue
): void => {
  header.updatedBy = issue?.createdFromAddress || ExternalContactName;
};

const assignSystemHeader = (
  header: Partial<IssueMessageHeader>,
  issueMessage: Message
): void => {
  header.fontStyle = styles.systemText;
  header.avatarIcon = getSystemAvatarIcon(issueMessage);
  header.avatarIconStyle = styles.systemAvatar;
  header.avatarIconColor = Colors.white;
  header.updatedBy = getSystemUpdatedBy(issueMessage);
};

const getSystemUpdatedBy = (issueMessage: Message): string => {
  return issueMessage.updatedBy.id === AnonymousId &&
    issueMessage.type === IssueMessageType.UserMessage
    ? AnonymousName
    : SystemName;
};

const getSystemAvatarIcon = (issueMessage: Message): string => {
  return issueMessage.updatedBy.id === AnonymousId &&
    issueMessage.type === IssueMessageType.UserMessage
    ? 'incognito'
    : 'robot';
};

const styles = StyleSheet.create({
  systemText: {
    color: Colors.role.textSecondary,
  },
  systemAvatar: {
    backgroundColor: Colors.role.textSecondary,
  },
});
